import { compute } from './compute';
import { ema } from './indicators';

/**
 * Calculates the Moving Average Convergence Divergence (MACD) indicator using compute philosophy
 * @param bars Array of objects
 * @param field Field to calculate MACD on (default: 'close')
 * @param fastPeriod Fast EMA period (default: 12)
 * @param slowPeriod Slow EMA period (default: 26)
 * @param signalPeriod Signal line period (default: 9)
 * @returns Array of objects with MACD values
 */
export const macd = <T>(
    bars: T[],
    field: keyof T = 'close' as keyof T,
    fastPeriod: number = 12,
    slowPeriod: number = 26,
    signalPeriod: number = 9
): Array<T & {
    emaFast: number | undefined;
    emaSlow: number | undefined;
    macdLine: number | undefined;
    signalLine: number | undefined;
    histogram: number | undefined;
}> => {
    // Step 1: Compute fast EMA
    const barsWithFastEMA = ema(bars, field, 'emaFast', fastPeriod);
    
    // Step 2: Compute slow EMA
    const barsWithSlowEMA = ema(barsWithFastEMA, field, 'emaSlow', slowPeriod);
    
    // Step 3: Compute MACD line (fast EMA - slow EMA)
    const barsWithMACDLine = compute(
        barsWithSlowEMA,
        'macdLine',
        (bar) => {
            if (bar.emaFast === undefined || bar.emaSlow === undefined) {
                return undefined;
            }
            return bar.emaFast - bar.emaSlow;
        }
    );
    
    // Step 4: Compute signal line (EMA of MACD line)
    const barsWithSignalLine = compute(
        barsWithMACDLine,
        'signalLine',
        (bar, index, allBars, params) => {
            if (bar.macdLine === undefined) {
                return undefined;
            }
            
            // Find first valid MACD index
            const firstMACDIndex = allBars.findIndex(b => b.macdLine !== undefined);
            const macdCount = index - firstMACDIndex + 1;
            
            if (macdCount < params.signalPeriod) {
                return undefined;
            }
            
            if (macdCount === params.signalPeriod) {
                // Initial SMA of MACD values
                let sum = 0;
                for (let i = firstMACDIndex; i <= index; i++) {
                    sum += allBars[i].macdLine || 0;
                }
                return sum / params.signalPeriod;
            }
            
            // EMA of MACD (signal line)
            const k = 2 / (params.signalPeriod + 1);
            const prevSignal = (allBars[index - 1] as any).signalLine;
            if (prevSignal === undefined) {
                return undefined;
            }
            return bar.macdLine * k + prevSignal * (1 - k);
        },
        { signalPeriod }
    );
    
    // Step 5: Compute histogram (MACD line - signal line)
    return compute(
        barsWithSignalLine,
        'histogram',
        (bar) => {
            if (bar.macdLine === undefined || bar.signalLine === undefined) {
                return undefined;
            }
            return bar.macdLine - bar.signalLine;
        }
    ) as Array<T & {
        emaFast: number | undefined;
        emaSlow: number | undefined;
        macdLine: number | undefined;
        signalLine: number | undefined;
        histogram: number | undefined;
    }>;
};